import React from 'react';
import { UI } from './typeScale';

/**
 * The rail: where you are, and where you can go.
 *
 * <h3>Five, not two</h3>
 *
 * The approved surface has five destinations. A rail that grew an icon each
 * time a screen landed would read as unfinished on every visit, so all five are
 * drawn and the three without a screen yet are disabled — "later" rather than
 * "broken", and a disabled control cannot take anyone to a page that is not
 * there.
 *
 * The estate stays open even when it is empty. An empty screen that says why
 * it is empty is worth more than a shut control that says nothing — and the
 * estate is scoped to one person in one tenant, so empty is a thing that needs
 * explaining rather than a reason to lock the door.
 *
 * <h3>Run is reachable without a selection</h3>
 *
 * Trust and Change describe one agent and need one chosen first. The Run Center
 * does its own choosing, from the agents the platform says serve governed
 * traffic, so it opens as soon as one of those exists.
 */
export default function ProtectRail({
  view = 'front', estateCount = 0, selected = false, runnable = 0, onGo,
}) {
  /* A shut control says why it is shut, in its own words: the three agent-shaped
     screens are shut for a different reason than the Run Center is, and the
     wrong reason sends somebody looking for a control that is not there. */
  const items = [
    { key: 'front', label: 'Protect an agent', icon: Shield, live: true },
    /* Open whether or not there is an estate. It was shut while empty, on the
       reasoning that nothing to show should mean nowhere to go — the same
       argument that had the dashboard delete its own chart, and wrong the same
       way. The estate is scoped to the signed-in person in the tenant they are
       signed in to, so empty can mean nothing was made, everything was
       deleted, or the agents are one tenant chip away. A button that will not
       open tells nobody which, and takes away the only screen that could. */
    { key: 'dashboard', label: 'Your agents', icon: Bars, live: true },
    /* Trust and Change are about one agent. Without one chosen they have no
       subject, so the rail keeps them shut rather than opening an empty page. */
    { key: 'trust', label: 'Trust', icon: Doc, live: selected,
      why: 'choose an agent first' },
    /* Run is not. The Run Center chooses its own agent, from the ones the
       platform says serve governed traffic, so it opens whenever there is at
       least one — reaching it should not require having just built something. */
    { key: 'run', label: 'Run', icon: Play, live: runnable > 0 || selected,
      why: 'no agent is published or execution ready yet' },
    { key: 'change', label: 'Change', icon: Gear, live: selected,
      why: 'choose an agent first' },
  ];

  return (
    /* ── The same five doors, turned ──────────────────────────────────────────
       A column beside the page on a wide screen; a scrollable row above it on a narrow one.
       It may NOT collapse to icons: that is what it was, with the name only in a `title`, so
       the route back to an agent appeared on hover, on a pointer, after a pause — and on a
       phone there is no hover at all. Named at every width, and the row scrolls rather than
       squeezing five labels into 390px.

       The breakpoint is why this is in classes. A 184px column written inline cannot have one,
       which is what left a phone with half its screen taken by the rail. */
    <nav
      aria-label="Protect"
      className={'flex shrink-0 items-stretch gap-0.5 overflow-x-auto border-b px-2 py-2'
        + ' lg:sticky lg:top-0 lg:h-screen lg:w-[184px] lg:flex-none lg:flex-col'
        + ' lg:overflow-x-visible lg:border-b-0 lg:border-r lg:px-2 lg:py-3'}
      style={{
        background: 'var(--p7-surface)',
        /* The width and the side come from the classes; the colour is this surface's. */
        borderColor: 'var(--p7-line)',
      }}
    >
      {/* No mark here: the header lockup already carries one, and two of the
          same logo a few pixels apart reads as a mistake rather than branding. */}
      {items.map(({ key, label, icon: Icon, live, why }) => {
        const here = view === key;
        return (
          <button
            key={key}
            type="button"
            title={live ? label : `${label} — ${why || 'not ready yet'}`}
            aria-label={label}
            aria-current={here ? 'page' : undefined}
            disabled={!live}
            onClick={() => live && onGo && onGo(key)}
            className="w-auto shrink-0 lg:w-full"
            style={{
              minHeight: '38px', border: 0, borderRadius: '9px',
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', textAlign: 'left',
              ...UI, fontWeight: here ? 600 : 400,
              cursor: live ? 'pointer' : 'default',
              background: here ? 'var(--p7-accent-bg)' : 'transparent',
              color: here ? 'var(--p7-accent)'
                : live ? 'var(--p7-ink-subtle)' : 'var(--p7-ink-faint)',
              opacity: live ? 1 : 0.45,
            }}
          >
            <Icon />
            {/* One string for the eye and for a screen reader — two spellings of
                the same door is how they drift apart. */}
            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis',
                           whiteSpace: 'nowrap' }}>{label}</span>
          </button>
        );
      })}
    </nav>
  );
}

const svg = (children) => (
  <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor"
       strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);

const Shield = () => svg(<path d="M12 3 4 7v5c0 4.4 3.4 8.3 8 9 4.6-.7 8-4.6 8-9V7l-8-4Z" />);
const Bars = () => svg(<path d="M4 19V9M10 19V5M16 19v-7M21 19H3" />);
const Doc = () => svg(<><path d="M6 4h9l4 4v12H6z" /><path d="M9 12h7M9 16h5" /></>);
const Play = () => svg(<><path d="M21 12a8 8 0 1 1-3-6.2" /><path d="M8 12h6M12 9l3 3-3 3" /></>);
const Gear = () => svg(<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1A1.7 1.7 0 0 0 9 19.4a1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1A1.7 1.7 0 0 0 4.6 9a1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" /></>);
